import { t } from '../../../shared/config/translations'
import { escapeHtml } from '../../utils/html'

type MultiSelectOption = {
	value: string
	label: string
	displayLabel?: string
	includeInAll?: boolean
}

export function renderMultiSelect({
	searchable = false,
	selectAll = false,
	allLabel,
	allSelected: defaultAllSelected,
	allValue = '',
	label,
	options,
	selectId,
	selected,
}: {
	searchable?: boolean
	selectAll?: boolean
	allLabel: string
	allSelected: boolean
	allValue?: string
	label: string
	options: MultiSelectOption[]
	selectId: string
	selected: string[]
}): string {
	const selectedValues = selected.filter((value) =>
		options.some((option) => option.value === value),
	)
	const selectedSet = new Set(selectedValues)
	const selectedLabels = options
		.filter((option) => selectedSet.has(option.value))
		.map((option) => option.displayLabel ?? option.label)
	const optionsIncludedInAll = options.filter(
		(option) => option.includeInAll !== false,
	)
	const allSelected =
		defaultAllSelected ||
		(selectAll &&
			optionsIncludedInAll.length > 0 &&
			optionsIncludedInAll.every((option) =>
				selectedSet.has(option.value),
			))
	const partiallySelected = selectAll && !allSelected && selectedSet.size > 0
	const selectedIndependentLabels = options
		.filter(
			(option) =>
				option.includeInAll === false && selectedSet.has(option.value),
		)
		.map((option) => option.displayLabel ?? option.label)
	const displayValue = allSelected
		? selectedIndependentLabels.length === 0
			? allLabel
			: selectedIndependentLabels.length === 1
				? selectedIndependentLabels[0]
				: `${selectedIndependentLabels.length} selected`
		: selectedLabels.length === 1
			? selectedLabels[0]
			: `${selectedLabels.length} selected`
	const optionMarkup = [
		{
			value: allValue,
			label: allLabel,
			selected: allSelected,
			allOption: true,
			includeInAll: true,
			indeterminate: partiallySelected,
		},
		...options.map((option) => ({
			...option,
			selected:
				option.includeInAll === false
					? selectedSet.has(option.value)
					: (selectAll && allSelected) ||
						(!allSelected && selectedSet.has(option.value)),
			allOption: false,
			indeterminate: false,
		})),
	]
		.map(
			(option) => `
                <button
                    type="button"
                    class="multi-select-option"
                    data-value="${escapeHtml(option.value)}"
                    data-label="${escapeHtml(option.label)}"
                    role="${selectAll ? 'option' : 'checkbox'}"
                    aria-label="${escapeHtml(option.label)}"
                    ${selectAll ? 'aria-selected' : 'aria-checked'}="${String(option.selected)}"
					data-all-option="${String(option.allOption)}"
					data-included-in-all="${String(option.includeInAll !== false)}"
					data-indeterminate="${String(option.indeterminate)}"
                >
                    <span class="multi-select-check"></span>
                    <span>${escapeHtml(option.displayLabel ?? option.label)}</span>
                </button>
            `,
		)
		.join('\n')

	return `
        <div
            class="tt-select tt-multi-select"
            data-searchable="${searchable}"
            data-select-all="${selectAll}"
            data-select-id="${escapeHtml(selectId)}"
            data-all-value="${escapeHtml(allValue)}"
            data-all-label="${escapeHtml(allLabel)}"
        >
            <button
                type="button"
                id="${escapeHtml(selectId)}"
                class="multi-select-trigger select-auto-width"
                aria-expanded="false"
            >
				<span class="f secondary">${escapeHtml(label)}</span>
                <span class="multi-select-value">${escapeHtml(displayValue)}</span>
                <svg><use href="#symbols-arrow-down"></use></svg>
            </button>
            <div class="multi-select-menu" role="${selectAll ? 'listbox' : 'group'}" aria-label="${escapeHtml(label)}" hidden>
                ${
					searchable
						? `
                    <div class="multi-select-search-header">
                        <div class="multi-select-search-row">
                            <svg class="multi-select-search-icon" viewBox="0 0 24 24" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m21 21-4.34-4.34"/><circle cx="11" cy="11" r="8"/></svg>
                            <input type="search" class="multi-select-search" aria-label="${escapeHtml(t('collection.searchFilter').replace('{filter}', label.toLowerCase()))}" placeholder="${escapeHtml(t('collection.searchFilter').replace('{filter}', label.toLowerCase()))}" autocomplete="off">
                            <button type="button" class="multi-select-clear-search" aria-label="${t('collection.clearFilterSearch')}"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-x preview-icon" aria-hidden="true"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg></button>
                        </div>

                    </div>
                `
						: ''
				}
                <div class="multi-select-options">${optionMarkup}</div>
                ${searchable ? `<p class="multi-select-empty" role="status" hidden>${t('collection.noFilterMatches')}</p>` : ''}
            </div>
        </div>
    `
}
